import { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * usePagination
 * =============
 * Small helper that keeps the page-slicing math out of every list component.
 * Pair it with the shared <Pagination /> component.
 *
 * Usage:
 *   const { page, setPage, pageCount, pageItems, totalItems, pageSize } =
 *     usePagination(filteredRows, 10, [typeFilter, ageTab]);
 *   ...
 *   {pageItems.map(...)}
 *   <Pagination page={page} pageCount={pageCount} totalItems={totalItems}
 *               pageSize={pageSize} onPageChange={setPage} />
 *
 * @param {Array}  items      the full (already filtered/sorted) array
 * @param {number} pageSize   rows per page (default 10)
 * @param {Array}  resetDeps  when any of these change, jump back to page 1
 */
export default function usePagination(items, pageSize = 10, resetDeps = []) {
  // Wrap in useMemo so the reference is stable across renders when `items`
  // hasn't changed — keeps the pageItems useMemo below from recomputing every
  // render.
  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const [page, setPage] = useState(1);

  const totalItems = list.length;
  const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));

  // Serialize the caller's reset dependencies into a single stable string key.
  // This gives the effect a real, statically-analyzable dependency array (no
  // variable, no spread) so the react-hooks lint rule is satisfied, while
  // preserving the behavior: reset to page 1 whenever any resetDep changes.
  const resetKey = JSON.stringify(resetDeps);

  // Reset to first page when the caller's dependencies change (filters, tab,
  // new data) so we never sit on a page that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  // Clamp if the list shrank below the current page.
  const current = Math.min(page, pageCount);

  const pageItems = useMemo(() => {
    const start = (current - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, current, pageSize]);

  const changePage = useCallback((p) => setPage(p), []);

  return {
    page: current,
    setPage: changePage,
    pageCount,
    pageItems,
    totalItems,
    pageSize,
  };
}
